import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, MapPin, CheckCircle2, Clock, Users, ArrowRight, Filter } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssociationsPage = () => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');

  useEffect(() => {
    const fetchAssociations = async () => {
      try {
        const response = await axios.get(`${API_URL}/associations`);
        setAssociations(response.data);
      } catch (_) { /* ignore */ } finally { setLoading(false); }
    };
    fetchAssociations();
  }, []);

  const filteredAssociations = associations.filter(assoc => {
    const matchesSearch = 
      (assoc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      assoc.short_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assoc.region.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRegion = regionFilter === 'all' || assoc.region === regionFilter;
    return matchesSearch && matchesRegion;
  });

  const nwCount = associations.filter(a => a.region === 'Northwest').length;
  const swCount = associations.filter(a => a.region === 'Southwest').length;

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Member Associations | CCA Montreal</title>
        <meta name="description" content="Explore the 13 Cameroonian cultural associations united under CCA Montreal. Northwest and Southwest regions represented." />
        <meta property="og:title" content="CCA Montreal - Member Associations" />
        <meta property="og:description" content="13 cultural associations united under CCA Montreal" />
      </Helmet>

      <Navbar variant="dark" />
      
      {/* Hero Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cameroon-green/8 via-transparent to-transparent" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 sm:pt-12 sm:pb-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-4">
              <Users className="w-4 h-4 text-cameroon-yellow" />
              <span className="text-sm text-white/70">13 Associations</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="page-title">
              Member Associations
            </h1>
            <p className="text-white/60 text-base sm:text-lg">
              Explore our Cameroonian cultural associations from the Northwest and Southwest regions, united under CCA Montreal.
            </p>
          </div>
          
          {/* Filters */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search associations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cameroon-green/50"
                data-testid="search-associations"
              />
            </div>
            <div className="flex gap-2">
              {[
                { value: 'all', label: `All (${associations.length})` },
                { value: 'Northwest', label: `Northwest (${nwCount})` },
                { value: 'Southwest', label: `Southwest (${swCount})` },
              ].map(opt => (
                <Button
                  key={opt.value}
                  variant="outline"
                  size="sm"
                  onClick={() => setRegionFilter(opt.value)}
                  className={`text-xs sm:text-sm ${
                    regionFilter === opt.value 
                      ? 'bg-cameroon-green/20 border-cameroon-green/40 text-cameroon-green' 
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                  data-testid={`filter-${opt.value.toLowerCase()}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array(12).fill(0).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden animate-pulse bg-white/5 border border-white/5">
                <div className="h-44 bg-white/5"></div>
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-white/10 rounded w-3/4"></div>
                  <div className="h-4 bg-white/5 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredAssociations.map((assoc) => {
              const t = assoc.theme || {};
              return (
                <Link 
                  key={assoc.id} 
                  to={`/${assoc.slug || assoc.short_name.toLowerCase()}`}
                  data-testid={`association-card-${assoc.short_name.toLowerCase().replace(/[\s()]/g, '-')}`}
                >
                  <Card className="overflow-hidden border transition-all duration-300 hover:-translate-y-1 group h-full bg-transparent" style={{ borderColor: `${t.primary || '#ffffff'}15` }}>
                    <CardContent className="p-0">
                      <div className="relative h-44 overflow-hidden">
                        <img 
                          src={assoc.image_url || assoc.logo_url || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={assoc.name || assoc.short_name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent"></div>
                        
                        {/* Status Badge */}
                        <div className="absolute top-3 right-3">
                          <Badge className={`text-[10px] ${
                            assoc.signature_status === 'signed' 
                              ? 'bg-emerald-500/80 text-white' 
                              : 'bg-amber-500/80 text-white'
                          }`}>
                            {assoc.signature_status === 'signed' ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Signed</> : <><Clock className="w-3 h-3 mr-1" /> Pending</>}
                          </Badge>
                        </div>
                        
                        {/* Region */}
                        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/80 text-xs">
                          <MapPin className="w-3 h-3" />
                          {assoc.region}
                        </div>

                        {/* Theme color accent */}
                        {t.primary && (
                          <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: t.primary }} />
                        )}
                      </div>
                      
                      <div className="p-4" style={{ backgroundColor: `${t.primary || '#ffffff'}05` }}>
                        <h3 className="font-serif font-semibold text-lg text-white group-hover:text-cameroon-yellow transition-colors">
                          {assoc.short_name}
                        </h3>
                        <p className="text-sm text-white/50 mt-1 line-clamp-2">
                          {assoc.description}
                        </p>
                        {assoc.president_name && (
                          <p className="text-xs text-white/30 mt-3">
                            President: {assoc.president_name}
                          </p>
                        )}
                        <div className="mt-3 flex items-center gap-1 text-xs font-medium group-hover:gap-2 transition-all" style={{ color: t.primary || '#4ade80' }}>
                          <span>View Page</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
        
        {!loading && filteredAssociations.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-white/15 mx-auto mb-4" />
            <p className="text-white/50">No associations found matching your search.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default AssociationsPage;
